<template>
  <div class="step">
    <!--  PROPERTY LAPTOP INSURANCE  -->
    <component
      :is="'question-property-laptop-insurance'"
      product-name="home"
      step-id="content"
      question-id="property-laptop-insurance"
      :show-confirm-modal="showConfirmModal"
      :form-observer="formObserver"
      :form-data="form"
      :step-data="step"
      @input="updateStepData"
    />

    <!--  SPECIFIC ITEMS INSURANCE  -->
    <component
      :is="'question-specific-items-insurance'"
      product-name="home"
      step-id="content"
      question-id="property-specific-items-insurance"
      :show-confirm-modal="showConfirmModal"
      :form-observer="formObserver"
      :form-data="form"
      :step-data="step"
      @input="updateStepData"
    />

    <!--  ALL RISKS UNSPECIFIED  -->
    <component
      :is="'question-all-risks-unspecified'"
      product-name="home"
      step-id="content"
      question-id="property-specific-items-insurance"
      :form-observer="formObserver"
      :form-data="form"
      :step-data="step"
      @input="updateStepData"
    />

    <!--  CONTENTS SUMS IN  -->
    <component
      :is="'question-contents-sums-in'"
      product-name="home"
      step-id="content"
      question-id="contents-sums-ins"
      has-contents-calculator
      :is-contents-calculator-shown="isContentsCalculatorShown"
      :form-observer="formObserver"
      :form-data="form"
      :step-data="step"
      :help-text="contentsSumsInHelpText"
      @toggle-calculator="isContentsCalculatorShown = $event"
      @input="updateStepData"
    />

    <!--  CONTENTS SUMS IN CALCULATOR  -->
    <contents-calculator
      v-if="isCoverTypeHadContents && isContentsCalculatorShown"
      @summarized="setContentsSumsIns"
      @close-calculator="closeCalculator"
    />

    <!--  PROPERTY PURCHASED  -->
    <component
      :is="'question-property-purchased'"
      product-name="home"
      step-id="content"
      question-id="property-purchased"
      :form-observer="formObserver"
      :form-data="form"
      :step-data="step"
      @input="updateStepData"
    />

    <!--  PROPERTY YEARS LIVED  -->
    <component
      :is="'question-property-years-lived'"
      product-name="home"
      step-id="content"
      question-id="property-years-lived"
      :form-observer="formObserver"
      :form-data="form"
      :step-data="step"
      @input="updateStepData"
    />

    <!--  PROPERTY OCCUPIED  -->
    <component
      :is="'question-base-radio-list'"
      question-id="property-occupied"
      product-name="home"
      step-id="content"
      title="Who is the property occupied by?"
      :custom-fields="{ field: 'propertyOccupied' }"
      :options-config="{
        options: constants.HOME_PROPERTY_OCCUPIED
      }"
      :help-text="{
        value: 'If you are living with your partner, girlfriend or boyfriend please select <b>Policyholder and Immediate Family</b>.',
      }"
      :form-observer="formObserver"
      :form-data="form"
      :step-data="step"
      :validation-errors="{
        field: {
          required: 'Please let us know who lives in your home.',
        },
      }"
      @input="updateStepData"
    />

    <!--  PROPERTY OCCUPIED BY -->
    <component
      :is="'question-property-occupied-by'"
      product-name="home"
      step-id="content"
      question-id="property-occupied"
      :form-observer="formObserver"
      :form-data="form"
      :step-data="step"
      @toast="$emit('toast', $event)"
      @input="updateStepData"
    />

    <!--  PROPERTY SMOKER  -->
    <component
      :is="'question-base-radio-boolean'"
      question-id="property-smoker"
      product-name="home"
      step-id="content"
      title="Does anybody living in the property smoke?"
      :custom-fields="{ field: 'propertySmoker' }"
      :help-text="{
        value: 'We need to ask this as insurance providers need to assess fire risks.',
        fullWidth: true
      }"
      :form-observer="formObserver"
      :form-data="form"
      :step-data="step"
      @input="updateStepData"
    />

    <!--  PROPERTY UNOCCUPIED  -->
    <component
      :is="'question-base-radio-list'"
      question-id="property-unoccupied"
      product-name="home"
      step-id="content"
      title="When is your property normally unoccupied?"
      :custom-fields="{ field: 'propertyUnoccupied' }"
      help-text="You are occupying the property if you are living in the property as the main home with enough furniture and facilities for daily normal living and sleeping purposes."
      :options-config="{
        options: constants.HOME_PROPERTY_UNOCCUPIED
      }"
      :form-observer="formObserver"
      :form-data="form"
      :step-data="step"
      :validation-errors="{
        field: {
          required: 'Please select when your property is normally occupied.',
        },
      }"
      @input="updateStepData"
    />

    <!--  PROPERTY UNOCCUPIED DAYS  -->
    <component
      :is="'question-property-unoccupied-days'"
      product-name="home"
      step-id="content"
      question-id="property-unoccupied-days"
      :form-observer="formObserver"
      :form-data="form"
      :step-data="step"
      :validation-errors="{
        propertyUnoccupiedDays: {
          is_not: 'Please select the number of days.',
        },
      }"
      :help-text="{
        value: `
          <p>How many days is the property left empty in a row because you are on holiday, visiting friends and family or away on business.</p>
          <p>If the property is left empty for more than 30 days TOTAL throughout the year, please select '31-45 days in a row' or higher, depending on your circumstances.</p>
        `,
        collapsible: isExperimentMinimiseHelptext,
        openerText: 'Defining Vacancy Periods',
      }"
      @input="updateStepData"
    />

    <!--  PROPERTY BUSINESS TYPE  -->
    <component
      :is="'question-property-business-type'"
      product-name="home"
      step-id="content"
      question-id="property-business-type"
      :form-observer="formObserver"
      :form-data="form"
      :step-data="step"
      :help-text="{
        value: 'This does not include working from home if you are normally office-based. However if you have regular business visitors to your home, run a business from home or if you have paying guests (e.g. holiday lets) please pick yes.',
        collapsible: isExperimentMinimiseHelptext,
        openerText: 'What are business purposes?',
        fullWidth: true,
      }"
      @input="updateStepData"
    />

    <!--  PROPERTY ALARM INSTALLED  -->
    <component
      :is="'question-property-alarm-installed'"
      product-name="home"
      step-id="content"
      question-id="property-alarm-installed"
      :form-observer="formObserver"
      :form-data="form"
      :step-data="step"
      @input="updateStepData"
    />

    <!--  PROPERTY MAINDOR LOCK  -->
    <component
      :is="'question-property-maindor-lock'"
      product-name="home"
      step-id="content"
      question-id="property-maindor-lock"
      :form-observer="formObserver"
      :form-data="form"
      :step-data="step"
      :validation-errors="{
        propertyMaindorLock: {
          required: 'Please select a lock type.',
        },
      }"
      :is-experiment-minimise-helptext="isExperimentMinimiseHelptext"
      @input="updateStepData"
    />

    <!--  PROPERTY PATIODOR LOCK  -->
    <component
      :is="'question-property-patiodor-lock'"
      product-name="home"
      step-id="content"
      question-id="property-patiodor-lock"
      :form-observer="formObserver"
      :form-data="form"
      :step-data="step"
      :validation-errors="{
        propertyPatiodorLock: {
          required: 'Please select a lock type.',
        },
      }"
      :is-experiment-minimise-helptext="isExperimentMinimiseHelptext"
      @input="updateStepData"
    />

    <!--  PROPERTY EXTERNALDOR LOCK  -->
    <component
      :is="'question-property-externaldor-lock'"
      product-name="home"
      step-id="content"
      question-id="property-extarnaldor-lock"
      :form-observer="formObserver"
      :form-data="form"
      :step-data="step"
      :validation-errors="{
        propertyExternaldorLock: {
          required: 'Please select a lock type.',
        },
      }"
      :is-experiment-minimise-helptext="isExperimentMinimiseHelptext"
      @input="updateStepData"
    />

    <!--  PROPERTY WINDOW LOCK  -->
    <component
      :is="'question-base-radio-boolean'"
      question-id="property-window-lock"
      product-name="home"
      step-id="content"
      title="Do all accessible windows (ground floor) have key operated locks?"
      :custom-fields="{ field: 'propertyWindowLock' }"
      :help-text="{
        value: 'Accessible windows are those on the ground floor, at basement level or within easy reach of ground level and capable of being opened.',
        fullWidth: true
      }"
      :form-observer="formObserver"
      :form-data="form"
      :step-data="step"
      @input="updateStepData"
    />

    <!--  PROPERTY TOTAL SMOKE ALARMS  -->
    <component
      :is="'question-base-radio-list'"
      question-id="property-total-smoke-alarms"
      product-name="home"
      step-id="content"
      title="How many working smoke alarms are there?"
      :custom-fields="{ field: 'propertyTotalSmokeAlarms' }"
      help-text="Please tell us how many operational smoke detectors the property has. Smoke detectors should be working and tested regularly."
      :options-config="{
        options: constants.HOME_PROPERTY_TOTAL_SMOKE_ALARMS
      }"
      :form-observer="formObserver"
      :form-data="form"
      :step-data="step"
      :validation-errors="{
        field: {
          required: 'Please select a number.',
        },
      }"
      @input="updateStepData"
    />

    <!--  PROPERTY LOCAL WATCH  -->
    <component
      :is="'question-base-radio-list'"
      question-id="property-local-watch"
      product-name="home"
      step-id="content"
      title="Is there a local Neighbourhood Watch?"
      :custom-fields="{ field: 'propertyLocalWatch' }"
      help-text="You may be entitled to a discount if you are a member of a police approved neighbourhood watch scheme."
      :options-config="{
        options: constants.HOME_PROPERTY_LOCAL_WATCH
      }"
      :form-observer="formObserver"
      :form-data="form"
      :step-data="step"
      @input="updateStepData"
    />
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import { cloneDeep } from 'lodash';

import getStepOptions from '@/helpers/home/getStepOptions';
import QuestionPropertyLaptopInsurance from '@/components/questions/home/PropertyLaptopInsurance';
import QuestionSpecificItemsInsurance from '@/components/questions/home/SpecificItemsInsurance';
import QuestionAllRisksUnspecified from '@/components/questions/home/AllRisksUnspecified';
import QuestionContentsSumsIn from '@/components/questions/home/ContentsSumsIn';
import QuestionPropertyPurchased from '@/components/questions/home/PropertyPurchased';
import QuestionPropertyYearsLived from '@/components/questions/home/PropertyYearsLived';
import QuestionPropertyOccupiedBy from '@/components/questions/home/PropertyOccupiedBy';
import QuestionPropertyUnoccupiedDays from '@/components/questions/home/PropertyUnoccupiedDays';
import QuestionPropertyBusinessType from '@/components/questions/home/PropertyBusinessType';
import QuestionPropertyAlarmInstalled from '@/components/questions/home/PropertyAlarmInstalled';
import QuestionPropertyMaindorLock from '@/components/questions/home/PropertyMaindorLock';
import QuestionPropertyPatiodorLock from '@/components/questions/home/PropertyPatiodorLock';
import QuestionPropertyExternaldorLock from '@/components/questions/home/PropertyExternaldorLock';
import useExperimentMinimiseHelptext from '@/composables/useExperimentMinimiseHelptext';

export default {
  name: 'form-home-step-2',
  components: {
    QuestionSpecificItemsInsurance,
    QuestionPropertyLaptopInsurance,
    QuestionAllRisksUnspecified,
    QuestionContentsSumsIn,
    QuestionPropertyPurchased,
    QuestionPropertyYearsLived,
    QuestionPropertyOccupiedBy,
    QuestionPropertyUnoccupiedDays,
    QuestionPropertyBusinessType,
    QuestionPropertyAlarmInstalled,
    QuestionPropertyMaindorLock,
    QuestionPropertyPatiodorLock,
    QuestionPropertyExternaldorLock,
  },
  props: {
    form: {
      type: Object,
      required: true,
    },
    formObserver: {
      type: Object,
      required: true,
    },
  },
  inject: [
    'helpers',
    'config',
    'constants',
    'inputCharsLimiters',
  ],
  data () {
    return {
      step: null,

      // contents sums in
      isContentsCalculatorShown: false,
    };
  },
  computed: {
    contentsSumsInHelpText () {
      const helpTextValueSection = '<strong>How to work this out</strong> - ';
      const helpTextValueArray = [
        `<p>
          ${this.isExperimentMinimiseHelptext ? '' : helpTextValueSection}Anything that isn't fixed in place would be included within this value i.e. beds, furniture, TVs, ornaments etc.
        </p>`,
        `<p>
          The sum should reflect the full replacement cost as new for all contents - Including the items mentioned in the above questions (bikes, laptops & personal possessions).
        </p>`,
      ];
      return {
        value: this.isExperimentMinimiseHelptext ? helpTextValueArray.join('') : helpTextValueArray,
        collapsible: this.isExperimentMinimiseHelptext,
        openerText: 'Estimating Replacement Value of Contents',
        fullWidth: true,
      };
    },
    home () {
      return this.form.home;
    },

    isCoverTypeHadContents () {
      const type = this.home.propertyCoverType;
      return type === 'BC' || type === 'C';
    },
  },
  watch: {
    step: {
      handler (val) {
        this.$emit('step-data', val);
      },
      immediate: true,
    },
  },
  created () {
    this.$emit('tracking', 'content');
    this.initStep();
  },
  methods: {
    ...mapActions(['showToast', 'showConfirmModal']),

    initStep () {
      this.step = cloneDeep(this.form.content);
    },

    setContentsSumsIns (sum) {
      this.step.contentsSumsIns = sum;
      this.closeCalculator();
    },

    closeCalculator () {
      this.isContentsCalculatorShown = false;
      this.$scrollTo('#contents-sums-ins');
    },
    onPersonsLimitExceed (personType) {
      this.showToast({
        type: 'info',
        text: `Please note: the maximum number of ${personType} here is 6+`,
      });
    },
    triggerErrorOnProviderFails (provider, observer) {
      const observerField = observer.fields && observer.fields['content_property-purchased'];
      return provider.failed || (observerField?.changed && observerField?.invalid);
    },
    updateStepData (questionData) {
      this.step = { ...this.step, ...questionData };
    },
  },
  setup () {
    // COMPOSABLES
    const { isExperimentMinimiseHelptext } = useExperimentMinimiseHelptext();

    return {
      options: getStepOptions(2),
      isExperimentMinimiseHelptext,
    };
  },
};
</script>
